import type { Id } from "@repo/convex/dataModel";
import type {
  DocumentEvidenceCarryover,
  DocumentEvidenceReference,
  EvidenceFileKind,
} from "@repo/shared/document-evidence-link";
import {
  Captions,
  ChevronDown,
  ChevronUp,
  Download,
  ExternalLink,
  Eye,
  ImageOff,
  Maximize2,
} from "lucide-react";
import { useLayoutEffect, useMemo, useState } from "react";
import { InlineReferenceMarkdown } from "@/components/ui/inline-reference-markdown";
import { MessageMarkdown } from "@/components/ui/message-markdown";
import { Popover, PopoverContent } from "@/components/ui/popover";
import { DocumentPreviewDialog } from "@/features/documents/components/document-preview-dialog";
import { TranscriptDialog } from "@/features/documents/components/transcript-dialog";
import { useDownloadTranscript } from "@/features/documents/hooks/use-download-transcript";
import {
  AttachmentPreviewDialog,
  type AttachmentPreviewDialogItem,
} from "@/features/project/components/attachment-preview-dialog";
import type { DocumentPreviewKind } from "@/lib/file-types";
import { cn } from "@/lib/utils";
import { EvidenceExcerpt } from "./evidence-excerpt";
import {
  EVIDENCE_SOURCE_OUTDATED_NOTE,
  type EvidencePlace,
  type EvidenceSourceGroup,
  groupEvidenceRows,
} from "./evidence-source-groups";
import { EVIDENCE_SOURCE_ICONS } from "./evidence-source-icons";

export const DOCUMENT_EVIDENCE_FIGURE_THUMBNAIL_TESTID = "document-evidence-figure-thumbnail";

/** One source, named once and carrying its one action. */
export const DOCUMENT_EVIDENCE_SOURCE_TESTID = "document-evidence-source";

/** One place a source was cited: where in it, and what was found there. */
export const DOCUMENT_EVIDENCE_CITATION_TESTID = "document-evidence-citation";

/** The line saying what a citation became in the document's newest version. */
export const DOCUMENT_EVIDENCE_CARRYOVER_TESTID = "document-evidence-carryover";

/** The action a transcribed recording's source offers in place of opening its audio. */
export const DOCUMENT_EVIDENCE_VIEW_TRANSCRIPT_TESTID = "document-evidence-view-transcript";

/** The action a source whose file this app can draw offers in place of handing over its bytes. */
export const DOCUMENT_EVIDENCE_VIEW_PREVIEW_TESTID = "document-evidence-view-preview";

/** The popover's fixed measure, in pixels. Used for the box and for placing it. */
const EVIDENCE_POPOVER_WIDTH_PX = 440;

/** How close to the pane's edge the popover may sit before it flips to right-aligned. */
const EVIDENCE_POPOVER_EDGE_GUTTER_PX = 16;

/** Widest a figure may draw: the popover's measure less its horizontal padding. */
const FIGURE_MAX_WIDTH_PX = 412;

/**
 * Tallest a figure may draw. The picture is meant to be read rather than recognized, so it
 * takes real vertical room; past this the citation under it stops being reachable.
 */
const FIGURE_MAX_HEIGHT_PX = 180;

/**
 * Shortest edge the figure *box* may collapse to. An extreme aspect ratio — a page-wide
 * banner, a tall sidebar strip — fits the caps as a few-pixel sliver nobody can click or
 * tap; the box stays at least finger-sized and the image letterboxes inside it. The image
 * itself is never upscaled to fill the clamp.
 */
const FIGURE_MIN_EDGE_PX = 44;

/**
 * How much of a figure's description is shown before it is offered rather than imposed. A
 * generated description runs to a paragraph, and the picture below it is what the reader
 * came for.
 */
const FIGURE_DESCRIPTION_CLAMP_CHARS = 260;

/** The two storage URLs one cited figure block resolves to; either may be gone on its own. */
export interface EvidenceFigureImageUrls {
  /** The downscaled variant when the figure has one, otherwise the full-size image. */
  thumbnailUrl: string | null;
  /** What the lightbox opens. */
  fullUrl: string | null;
}

/**
 * One resolved citation row for a cited document passage. The provider owns resolution
 * (presentation lookup, source URLs, figure image URLs) so this component stays a pure
 * rendering of a citation group.
 */
export interface DocumentCitationRow {
  kind: "document";
  /** Locator key — also the dedupe identity for two citations of the same passage. */
  key: string;
  /**
   * The passage this row resolves on demand. Absent when the row arrived already resolved —
   * a knowledge item's document source is presented by the server, and there is nothing left
   * for the interaction provider to look up.
   */
  reference?: DocumentEvidenceReference;
  /**
   * Which document this is, for a row that carries no locator to read it from — a
   * server-resolved knowledge source. It is the grouping identity and what an openable URL
   * is minted against.
   */
  artifactId?: string;
  documentName: string;
  /**
   * The bytes the source opens as: the name they arrived under and their media type. Both are
   * absent for a link artifact, whose source is a web page rather than a file, and until the
   * presentation resolves.
   */
  fileName?: string;
  mimeType?: string;
  /** Which glyph stands for the document. Absent until the presentation resolves. */
  fileKind?: EvidenceFileKind;
  /** The cited passage itself, or `null` while loading and when it is unavailable. */
  excerpt: string | null;
  /** `Page 3 · heading → heading`, capped, or `null` when the block carries neither. */
  location: string | null;
  status: "loading" | "available" | "unavailable";
  sourceUrl: string | null;
  /**
   * Set only when the cited document is a recording's transcript, and then the source is read
   * in the transcript viewer rather than opened as a file. `blockIndex` is the turn to land on,
   * absent when the citation names a revision the viewer no longer reads.
   */
  transcript?: { blockIndex?: number };
  /**
   * The document has been read again from a newer source since this passage was cited. Set only
   * where that is true, and drawn once per document rather than per passage — it is a fact about
   * the file, and repeating it under every quote from the same file would say it three times.
   */
  sourceOutdated?: true;
  /** What this citation became in the document's newest version, when its owner was assessed. */
  carryover?: DocumentEvidenceCarryover;
  /** Set only when the cited block renders an image, and then it is always laid out. */
  figure?: {
    /** Intrinsic size of the figure, known before any URL is: it sizes the placeholder. */
    width: number;
    height: number;
    /**
     * `null` until the on-demand URL resolution answers — the row shows a placeholder in the
     * space the image will take. Once it answers, two nulls mean the image itself is gone
     * and the row shows a broken-image box instead.
     */
    urls: EvidenceFigureImageUrls | null;
  };
}

/**
 * One resolved citation row for a source that is not a document: a decision someone made
 * through `ask_user`, a clarification thread that closed, or a knowledge item somebody typed.
 *
 * These carry no locator and no excerpt — nothing here is a quote. What they carry is what the
 * record says now, resolved when the row was built, plus who settled it and when.
 */
export interface KnowledgeSourceCitationRow {
  kind: "decision" | "clarification" | "manual";
  key: string;
  /** The question that was asked, or the thread's title. A manual entry has neither. */
  heading: string | null;
  /** What settled it. A manual entry has no answer separate from its statement. */
  answer: string | null;
  /**
   * What a hand-written entry says. It lives on the item rather than on the source, so it
   * is only set when the citing surface hands it over.
   */
  statement?: string | null;
  /** Where the record itself can be opened. Only a clarification thread has one. */
  href?: string | null;
  /** Who settled it and when, already formatted for reading. */
  attribution: string;
  supportNote: string | null;
}

export type EvidenceCitationRow = DocumentCitationRow | KnowledgeSourceCitationRow;

/**
 * Which end of the marker the popover hangs from. It is a fixed 440px box, so a marker near
 * the right of the pane would push it off screen: there it hangs from the marker's right
 * edge instead, which keeps the citation under the word that carries it either way.
 */
export function resolveEvidencePopoverAlign(
  anchorLeft: number,
  viewportWidth: number,
  popoverWidth: number = EVIDENCE_POPOVER_WIDTH_PX
): "start" | "end" {
  return anchorLeft + popoverWidth + EVIDENCE_POPOVER_EDGE_GUTTER_PX > viewportWidth
    ? "end"
    : "start";
}

/** Whether a figure's description is long enough to be worth collapsing. */
export function shouldOfferFigureDescriptionExpansion(description: string): boolean {
  return description.trim().length > FIGURE_DESCRIPTION_CLAMP_CHARS;
}

/**
 * Scales a figure to reading size without cropping or upscaling it. This is the *image's*
 * rendered size; the interactive box around it additionally clamps each edge to
 * {@link FIGURE_MIN_EDGE_PX}, and every state of the figure reserves that same box so the
 * popover does not jump.
 */
function fitFigure(width: number, height: number): { width: number; height: number } | null {
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    // Dimensions this bad cannot reserve honest space; the caller falls back to the caps.
    return null;
  }
  const scale = Math.min(1, FIGURE_MAX_WIDTH_PX / width, FIGURE_MAX_HEIGHT_PX / height);
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * The figure a citation points at, at reading size. The box is sized from the figure's
 * intrinsic dimensions before, during and after URL resolution — clamped to a clickable
 * minimum edge — so the citation list never reflows under the reader; only what fills it
 * changes. The whole box opens the lightbox, and says so with the corner affordance.
 */
function FigureThumbnail({
  documentName,
  figure,
  onOpen,
}: {
  documentName: string;
  figure: NonNullable<DocumentCitationRow["figure"]>;
  onOpen: (item: AttachmentPreviewDialogItem) => void;
}) {
  const label = `Figure from ${documentName}`;
  const fit = fitFigure(figure.width, figure.height);
  const box = fit
    ? {
        width: Math.max(fit.width, FIGURE_MIN_EDGE_PX),
        height: Math.max(fit.height, FIGURE_MIN_EDGE_PX),
      }
    : { width: FIGURE_MAX_WIDTH_PX, height: FIGURE_MAX_HEIGHT_PX };

  if (!figure.urls) {
    return (
      <div
        className="max-w-full animate-pulse border border-border bg-muted"
        style={box}
        aria-hidden="true"
      />
    );
  }

  // Either URL alone is enough to show something: a figure with no downscaled variant shows
  // the original, and one whose original is gone is still worth showing at thumbnail size.
  const thumbnailUrl = figure.urls.thumbnailUrl ?? figure.urls.fullUrl;
  const fullUrl = figure.urls.fullUrl ?? figure.urls.thumbnailUrl;
  if (!thumbnailUrl || !fullUrl) {
    return (
      <div
        className="flex max-w-full flex-col items-center justify-center gap-1 border border-border bg-muted px-2 text-center text-[0.6875rem] text-muted-foreground"
        style={box}
      >
        <ImageOff className="size-4 shrink-0" aria-hidden="true" />
        <span className="line-clamp-2">Image unavailable</span>
      </div>
    );
  }

  return (
    <button
      type="button"
      // The click is the figure's alone: the citation marker behind the popover and the
      // surrounding row must not also react to opening the lightbox.
      onClick={(event) => {
        event.stopPropagation();
        onOpen({ fileName: documentName, url: fullUrl });
      }}
      aria-label={label}
      title={label}
      className="relative inline-flex max-w-full items-center justify-center overflow-hidden border border-border bg-muted outline-none hover:border-primary/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      style={box}
      data-testid={DOCUMENT_EVIDENCE_FIGURE_THUMBNAIL_TESTID}
    >
      {/* The image keeps its fitted size rather than filling the clamped box: stretching a
          sliver of a banner to 44px tall would upscale it into a blur. */}
      <img
        src={thumbnailUrl}
        alt={label}
        loading="lazy"
        className="max-h-full max-w-full object-contain"
        style={fit ?? undefined}
      />
      <span
        aria-hidden="true"
        className="absolute right-1.5 bottom-1.5 inline-flex items-center gap-1 border border-border bg-popover/90 px-1.5 py-0.5 text-[0.6875rem] leading-4 text-muted-foreground"
      >
        Enlarge
        <Maximize2 className="size-2.5 shrink-0" />
      </span>
    </button>
  );
}

/**
 * A figure's description, offered in full rather than imposed. A generated description runs
 * to a paragraph and the picture is what the reader came for, so a long one is clamped with
 * the rest one click away.
 */
function FigureDescription({ description }: { description: string }) {
  const [expanded, setExpanded] = useState(false);
  const collapsible = shouldOfferFigureDescriptionExpansion(description);

  return (
    <>
      <p
        className={cn(
          "min-w-0 whitespace-pre-wrap break-words text-xs/[1.6] text-muted-foreground",
          collapsible && !expanded && "line-clamp-5"
        )}
      >
        {description}
      </p>
      {collapsible ? (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((previous) => !previous);
          }}
          className="inline-flex items-center gap-1 text-[0.6875rem] leading-4 text-muted-foreground outline-none hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
        >
          {expanded ? "Show less" : "Show more"}
          {expanded ? (
            <ChevronUp className="size-2.5 shrink-0" aria-hidden="true" />
          ) : (
            <ChevronDown className="size-2.5 shrink-0" aria-hidden="true" />
          )}
        </button>
      ) : null}
    </>
  );
}

/** One measure for the action, whether it leaves the page or opens a surface in it. */
const SOURCE_ACTION_CLASS =
  "inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap border border-border px-1.5 py-0.5 text-[0.6875rem] leading-4 text-muted-foreground outline-none hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring";

/** The one action a source offers, or the authorship line that stands in its place. */
function SourceAction({
  group,
  onViewPreview,
  onViewTranscript,
}: {
  group: EvidenceSourceGroup;
  onViewPreview: (target: PreviewTarget) => void;
  onViewTranscript: (target: TranscriptTarget) => void;
}) {
  const action = group.action;
  if (action?.kind === "link") {
    const Icon = action.icon === "download" ? Download : ExternalLink;
    return (
      <a
        href={action.href}
        target="_blank"
        rel="noopener noreferrer"
        className={SOURCE_ACTION_CLASS}
      >
        {action.label}
        <Icon className="size-2.5 shrink-0" aria-hidden="true" />
      </a>
    );
  }
  if (action?.kind === "preview") {
    return (
      <button
        type="button"
        // The press belongs to the action alone: the citation marker behind the popover must
        // not also react to opening the file.
        onClick={(event) => {
          event.stopPropagation();
          onViewPreview({
            previewKind: action.previewKind,
            artifactId: action.artifactId,
            name: action.name,
            fileName: action.fileName,
            mimeType: action.mimeType,
            url: action.url,
          });
        }}
        className={SOURCE_ACTION_CLASS}
        data-testid={DOCUMENT_EVIDENCE_VIEW_PREVIEW_TESTID}
      >
        {action.label}
        <Eye className="size-2.5 shrink-0" aria-hidden="true" />
      </button>
    );
  }
  if (action?.kind === "transcript") {
    return (
      <button
        type="button"
        // The press belongs to the action alone: the citation marker behind the popover must
        // not also react to opening the transcript.
        onClick={(event) => {
          event.stopPropagation();
          onViewTranscript({
            artifactId: action.artifactId,
            name: action.name,
            processingRevisionId: action.processingRevisionId,
            blockIndex: action.blockIndex,
          });
        }}
        className={SOURCE_ACTION_CLASS}
        data-testid={DOCUMENT_EVIDENCE_VIEW_TRANSCRIPT_TESTID}
      >
        {action.label}
        <Captions className="size-2.5 shrink-0" aria-hidden="true" />
      </button>
    );
  }
  if (group.meta) {
    return (
      <span className="shrink-0 whitespace-nowrap text-[0.6875rem] leading-4 text-neutral-dim">
        {group.meta}
      </span>
    );
  }
  return null;
}

/**
 * One place a source was cited. It reads as location then quote, and it is complete on its
 * own: the passage is never hidden behind a second click.
 */
function PlaceBlock({
  documentName,
  first,
  place,
  references,
  onOpenFigure,
}: {
  documentName: string;
  first: boolean;
  place: EvidencePlace;
  /** See {@link EvidenceSourceGroup.references}. */
  references: boolean;
  onOpenFigure: (item: AttachmentPreviewDialogItem) => void;
}) {
  const figure = place.figure;
  return (
    <div
      className={cn(
        "flex flex-col items-start gap-1.5 px-3.5 pb-3",
        // A rule separates one reference from the next — never a source's name from its
        // own content, so the first place under a name never carries one.
        first ? "pt-2" : "border-t border-border pt-2.5"
      )}
      data-testid={DOCUMENT_EVIDENCE_CITATION_TESTID}
    >
      {place.location ? (
        <p className="min-w-0 break-words text-[0.6875rem] leading-4 text-neutral-dim">
          {place.location}
        </p>
      ) : null}
      {place.carryoverLine ? (
        <p
          className="min-w-0 break-words text-xs/[1.4] text-muted-foreground"
          data-testid={DOCUMENT_EVIDENCE_CARRYOVER_TESTID}
        >
          {place.carryoverLine}
        </p>
      ) : null}
      {place.excerpt !== null && place.excerpt !== "" ? (
        figure ? (
          // A figure's own words are its description, not a quotation from the page.
          <FigureDescription description={place.excerpt} />
        ) : place.table ? (
          // A table is already a bordered shape of its own; a quote rule beside it reads as
          // a second frame around the same thing.
          <div className="min-w-0 max-w-full text-xs/[1.6] text-muted-foreground">
            <EvidenceExcerpt excerpt={place.excerpt} />
          </div>
        ) : (
          <blockquote
            className={cn(
              "min-w-0 max-w-full break-words border-l-2 border-neutral-border pl-3 text-muted-foreground",
              place.monospace ? "font-mono text-[0.6875rem]/[1.5]" : "text-xs/[1.6]"
            )}
          >
            {references ? (
              // The same renderer the transcript shows this answer with, so a list or a link
              // the person typed survives. A soft break inside a paragraph is kept because one
              // decision's several answers arrive as one newline-joined string.
              <MessageMarkdown
                content={place.excerpt}
                className="text-xs/[1.6] text-muted-foreground [&_p]:mb-1 [&_p:last-child]:mb-0 [&_p]:whitespace-pre-line"
              />
            ) : (
              <EvidenceExcerpt excerpt={place.excerpt} />
            )}
          </blockquote>
        )
      ) : null}
      {place.tableRows ? (
        <p className="text-[0.6875rem] leading-4 text-neutral-dim">
          {`Rows ${place.tableRows.start}–${place.tableRows.end} of ${place.tableRows.total} · scroll inside the table to see the rest`}
        </p>
      ) : null}
      {figure ? (
        <FigureThumbnail documentName={documentName} figure={figure} onOpen={onOpenFigure} />
      ) : null}
      {place.supportNote ? (
        <p className="text-[0.6875rem] leading-4 text-neutral-dim">{place.supportNote}</p>
      ) : null}
    </div>
  );
}

/**
 * The title of a source written in chat: a question the model asked, or a thread's title.
 * Several questions arrive as one newline-joined string and each gets its own line, parsed
 * on its own so a mention in one is a chip and no line inherits block structure from the next.
 */
function AuthoredTitle({ title }: { title: string }) {
  const lines = title.split("\n").filter((line) => line.trim().length > 0);
  return (
    <>
      {lines.map((line, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: lines are derived from a stable string and never reorder.
        <span key={index} className="block">
          <InlineReferenceMarkdown content={line} />
        </span>
      ))}
    </>
  );
}

/** The file a preview press opens, and what the dialog draws and states about it. */
interface PreviewTarget {
  previewKind: DocumentPreviewKind;
  artifactId: string;
  name: string;
  fileName: string;
  mimeType: string | null;
  url: string;
}

/** The recording a "View transcript" press opens, which transcript of it, and where it landed. */
interface TranscriptTarget {
  artifactId: string;
  name: string;
  /** The transcription the citation was made against; `null` opens the current one. */
  processingRevisionId: string | null;
  blockIndex: number | null;
}

/** A source: what it is and how to open it, then every place it was cited. */
function SourceBlock({
  first,
  group,
  onOpenFigure,
  onViewPreview,
  onViewTranscript,
}: {
  first: boolean;
  group: EvidenceSourceGroup;
  onOpenFigure: (item: AttachmentPreviewDialogItem) => void;
  onViewPreview: (target: PreviewTarget) => void;
  onViewTranscript: (target: TranscriptTarget) => void;
}) {
  const Icon = EVIDENCE_SOURCE_ICONS[group.icon];
  return (
    <section
      className={cn("flex flex-col", first ? null : "border-t border-border")}
      data-testid={DOCUMENT_EVIDENCE_SOURCE_TESTID}
    >
      <div className="flex items-center justify-between gap-2 px-3.5 pt-3">
        <span className="flex min-w-0 items-center gap-1.5">
          <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <span className="min-w-0 break-words text-xs/[1.4] font-medium text-foreground">
            {group.references ? <AuthoredTitle title={group.title} /> : group.title}
          </span>
        </span>
        <SourceAction
          group={group}
          onViewPreview={onViewPreview}
          onViewTranscript={onViewTranscript}
        />
      </div>
      {group.sourceOutdated ? (
        <p className="px-3.5 pt-1 text-xs/[1.4] text-muted-foreground">
          {EVIDENCE_SOURCE_OUTDATED_NOTE}
        </p>
      ) : null}
      {group.places.map((place, index) => (
        <PlaceBlock
          key={place.key}
          documentName={group.title}
          first={index === 0}
          place={place}
          references={group.references}
          onOpenFigure={onOpenFigure}
        />
      ))}
    </section>
  );
}

/**
 * The citation list for one activated group of evidence markers, anchored under the marker
 * that was clicked. Escape and outside clicks close it (Base UI default).
 *
 * A text row is complete on its own and opens nothing further. Three rows are exceptions, and
 * each opens a surface that is a *sibling* of the popover rather than content inside
 * `PopoverContent`, which unmounts on close — a dialog's backdrop counts as an outside press,
 * so opening one may close the popover underneath it, and what it opened must outlive the list
 * that offered it. A figure row opens the full-size image in the shared lightbox, because the
 * picture is drawn at a reading size. A transcribed recording's source opens the transcript
 * viewer at the cited turn, because the recording's own bytes are audio. A source whose file
 * this app can draw opens the Documents screen's preview dialog on it, which is what that row
 * offers everywhere else.
 */
export function EvidenceCitationsPopover({
  anchor,
  onOpenChange,
  open,
  projectId,
  rows,
}: {
  anchor: HTMLElement | null;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  projectId: Id<"projects">;
  rows: readonly EvidenceCitationRow[];
}) {
  const [figurePreview, setFigurePreview] = useState<AttachmentPreviewDialogItem | null>(null);
  const [transcriptTarget, setTranscriptTarget] = useState<TranscriptTarget | null>(null);
  // The moment the preview was opened, which is both "it is open" and the clock its header
  // would read a distance against, exactly as the Documents row holds it.
  const [previewTarget, setPreviewTarget] = useState<(PreviewTarget & { openedAt: number }) | null>(
    null
  );
  const [align, setAlign] = useState<"start" | "end">("start");
  const { downloadTranscript, isDownloadingTranscript } = useDownloadTranscript(projectId);

  // Measured rather than left to collision handling: which end the box hangs from is the
  // difference between a citation under the cited word and one shunted across the pane.
  useLayoutEffect(() => {
    if (!open || !anchor || typeof window === "undefined") return;
    const measure = () =>
      setAlign(resolveEvidencePopoverAlign(anchor.getBoundingClientRect().left, window.innerWidth));
    measure();
    // A pane narrowed under an open popover moves the marker; the box has to follow it or
    // it ends up hanging off the edge it was placed to avoid.
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [anchor, open]);

  const groups = useMemo(() => groupEvidenceRows(rows), [rows]);
  return (
    <>
      <Popover open={open && anchor !== null && rows.length > 0} onOpenChange={onOpenChange}>
        <PopoverContent
          anchor={anchor}
          side="bottom"
          align={align}
          aria-label="Document evidence"
          // One fixed measure, whatever the evidence is: wide content scrolls inside its own
          // box rather than deciding how wide the citation list gets.
          className="w-[27.5rem] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden p-0"
          data-testid="document-evidence-citations"
        >
          {/* Past four or five references the list scrolls; the marker already said how many
              there are, so nothing here repeats the count. */}
          <div className="max-h-[min(24rem,60vh)] overflow-y-auto">
            {groups.map((group, index) => (
              <SourceBlock
                key={group.key}
                first={index === 0}
                group={group}
                onOpenFigure={setFigurePreview}
                onViewPreview={(target) => {
                  setPreviewTarget({ ...target, openedAt: Date.now() });
                  // The file fills the screen, and the citation list it was opened from would
                  // sit under it saying the same thing.
                  onOpenChange(false);
                }}
                onViewTranscript={(target) => {
                  setTranscriptTarget(target);
                  // The transcript is a full-screen read, and the citation list it was opened
                  // from would sit under it saying the same thing.
                  onOpenChange(false);
                }}
              />
            ))}
          </div>
        </PopoverContent>
      </Popover>
      <AttachmentPreviewDialog
        attachment={figurePreview}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setFigurePreview(null);
        }}
      />
      {/* Mounted only while a transcript is being read: the viewer subscribes to the recording's
          speaker names and binds their rename, and a popover nobody opened a transcript from has
          no business holding either. */}
      {transcriptTarget === null ? null : (
        <TranscriptDialog
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setTranscriptTarget(null);
          }}
          projectId={projectId}
          artifactId={transcriptTarget.artifactId}
          processingRevisionId={transcriptTarget.processingRevisionId}
          name={transcriptTarget.name}
          focusBlockIndex={transcriptTarget.blockIndex}
          onDownload={() => {
            void downloadTranscript(
              transcriptTarget.artifactId,
              transcriptTarget.name,
              transcriptTarget.processingRevisionId
            );
          }}
          isDownloading={isDownloadingTranscript(transcriptTarget.artifactId)}
        />
      )}
      {/* Mounted only while a file is being read, on the same terms as the transcript viewer.
          The header states what the citation carries — the file's own type — and omits the size
          and the upload date, which a citation does not carry. */}
      {previewTarget === null ? null : (
        <DocumentPreviewDialog
          open
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setPreviewTarget(null);
          }}
          previewKind={previewTarget.previewKind}
          url={previewTarget.url}
          name={previewTarget.name}
          fileName={previewTarget.fileName}
          mimeType={previewTarget.mimeType}
          sizeBytes={null}
          uploadedAt={null}
          now={previewTarget.openedAt}
        />
      )}
    </>
  );
}
